import { useFormik } from "formik";
import * as Yup from "yup";
import {
  TextField,
  MenuItem,
  FormControlLabel,
  Button,
  Grid,
  Typography,
  Box,
  Breadcrumbs,
  Divider,
  Card,
  InputLabel,
  InputAdornment,
  FormControl,
  Select,
  Stack,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CheckBox,
  Snackbar,
  Alert,
  Chip,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Link from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ClearIcon from "@mui/icons-material/Clear";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import Layout from "../../layout/Layout";
import ImageUploader from "./ImageUpload";

import { useEffect, useRef, useState } from "react";
import { apiData } from "../../services/api_service";
import {
  fetchProductDropdownAPI,
  fetchProductUnitAPI,
  updatestockadjustmentsAPI,
  updateStockBatchPriceAPI,
  storesDropdownAPI,
  stockTransferDataAPI,
  reservedStockDetailsAPI,
} from "../../config";
import { AlertMessage } from "../../components/AlertMessage";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { LoadingButton } from "@mui/lab";
import { Backdrop, CircularProgress } from "@mui/material";
import { formatAED, formatNumber } from "../../utils/format";

// MUI number fields hand back "" when empty; cast that (and null) to
// undefined so an OPTIONAL number field doesn't trip Yup's "must be a
// number" type error.
const num = () =>
  Yup.number().transform((val, orig) =>
    orig === "" || orig === null ? undefined : val
  );

// Manufacture date, expiry days and pricing only apply when ADDING stock
// (a brand-new batch is being created). For a DEDUCT the user picks an
// existing batch, so those fields are filled FROM that batch — never typed —
// and must not be required here. Requiring them unconditionally was the
// "Life of Days is required" error that blocked an otherwise valid deduct.
const validationSchema = Yup.object({
  product_id: Yup.number().required("Product is required"),
  store_id: Yup.number().required("Store is required"),
  adjustmentType: Yup.string().required("Adjustment Type is required"),
  stockAdjustment: Yup.number().required("Quantity is required").min(1),
  buying_price: num().when("adjustmentType", {
    is: "add",
    then: num().required("Buying Price is required").min(0),
    otherwise: num().notRequired(),
  }),
  selling_price: num().when("adjustmentType", {
    is: "add",
    then: num()
      .required("Selling price is required")
      .min(0, "Selling price cannot be negative")
      .test(
        "is-greater",
        "Selling price cannot be lesser than buying price",
        function (value) {
          const { buying_price } = this.parent;
          return value >= (buying_price || 0);
        }
      ),
    otherwise: num().notRequired(),
  }),
  manufacture_date: Yup.date().when("adjustmentType", {
    is: "add",
    then: Yup.date().required("Manufacture Date is required"),
    otherwise: Yup.date().notRequired(),
  }),
  life_days: num().when("adjustmentType", {
    is: "add",
    then: num().required("Life of Days is required").min(0),
    otherwise: num().notRequired(),
  }),
});

const InventoryManagement = () => {
  const user = JSON.parse(localStorage.getItem("user"));

  const alertRef = useRef();
  const { id } = useParams();

  const navigate = useNavigate();
  const [productOptions, setProductOptions] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [searchTerm, setSearchTerm] = useState([""]);
  const [storeOptions, setStoreOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  // Deduct flow: the existing batch the user chose to pull stock from.
  const [selectedDeductBatch, setSelectedDeductBatch] = useState(null);
  // Units of this product+store already reserved for pending orders/CDNs.
  // A deduct must never push physical stock below this (see deductMax).
  const [reservedQty, setReservedQty] = useState(0);
  const [stockData, setStockData] = useState([]);
  const [readOnlyMode, setReadOnlyMode] = useState(false);
  // How the selling price is typed: "excl" = without VAT, "incl" = with VAT.
  // The saved selling_price is ALWAYS the price without VAT; with "incl" the
  // typed number is converted using the chosen Tax (VAT) %.
  const [priceMode, setPriceMode] = useState("excl");
  const [sellingInput, setSellingInput] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("error"); // "success" | "error" | "warning" | "info"
  const [messageOpen, setMessageOpen] = useState(false);

  // Editing an already-added batch's price (see "Check Expiry" dialog table
  // below) — separate from the Add Stock form above, which only ever
  // creates a NEW batch. editingBatchId is the tbl_live_stock row being
  // corrected; null means no row is in edit mode.
  const [editingBatchId, setEditingBatchId] = useState(null);
  const [editBuyingPrice, setEditBuyingPrice] = useState("");
  const [editSellingPrice, setEditSellingPrice] = useState("");
  const [savingBatchPrice, setSavingBatchPrice] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleMessageClose = () => setMessageOpen(false);

  const handleSelect = (row) => {
    const key = `${row.product_id}-${row.store_id}-${row.manufacture_date}-${row.life_days}-${row.buying_price}`;

    // If same key clicked → unselect
    if (selectedRow === key) {
      setSelectedRow(null);
      formik.setFieldValue("manufacture_date", "");
      formik.setFieldValue("life_days", "");
      setReadOnlyMode(false);
    } else {
      setSelectedRow(key);
      formik.setFieldValue("manufacture_date", row.manufacture_date);
      formik.setFieldValue("life_days", row.life_days);
      formik.setFieldValue("buying_price", row.buying_price);
      formik.setFieldValue("selling_price", row.selling_price);
      setPriceMode("excl");
      setReadOnlyMode(true);
    }
  };

  const formik = useFormik({
    initialValues: {
      product_id: "",
      store_id: "",
      adjustmentType: "",
      stockAdjustment: "",
      buying_price: "",
      selling_price: "",
      tax_percent: "",
      manufacture_date: dayjs().format("YYYY-MM-DD"),
      life_days: "",
      note: "",
      out_of_stock: 0,
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (values.adjustmentType === "add") {
          if (values.buying_price <= 0) {
            setMessage("Buying Price cannot be 0 or empty");
            setMessageType("error");
            setMessageOpen(true);
            return;
          }

          if (values.selling_price <= 0) {
          setMessage("Selling price cannot be 0 or empty");
          setMessageType("warning");
          setMessageOpen(true);
          return;
        }
        }

        if (values.adjustmentType === "deduct") {
          if (!selectedDeductBatch) {
            setMessage("Please select a batch to deduct from");
            setMessageType("error");
            setMessageOpen(true);
            return;
          }
          if (Number(values.stockAdjustment) > deductMax) {
            setMessage(
              reservedIsBinding
                ? `Only ${formatNumber(
                    deductMax
                  )} unit(s) can be deducted — ${formatNumber(
                    reservedQty
                  )} reserved for pending orders cannot be removed`
                : `Only ${formatNumber(
                    deductMax
                  )} unit(s) available in the selected batch`
            );
            setMessageType("error");
            setMessageOpen(true);
            return;
          }
        }

        setLoading(true);

        const payload = {
          ...values,
          user_id: user.emp_id,
        };

        const result = await apiData(
          "post",
          updatestockadjustmentsAPI,
          payload
        );

        if (result.success) {
          alertRef.current.triggerAlert(
            JSON.stringify({
              message: result.msg,
              messageType: "success",
            })
          );
          // Deliberately NOT resetting `loading` here — this used to clear
          // it in the shared `finally` below immediately after the
          // request resolved, which re-enabled Submit for the full 2s the
          // success toast sits on screen before navigate() below actually
          // leaves the page. A user who didn't notice the toast (or just
          // clicked twice out of habit) could resubmit the exact same
          // add/deduct in that window — this is genuinely how the
          // duplicate 300-unit stock entries a client reported turned out
          // to happen. Leaving it true keeps the button disabled straight
          // through to the redirect; there's no case where staying on this
          // page with a re-enabled button after a successful submit is
          // useful.
          setTimeout(() => {
            navigate("/reports/adjustment-report");
          }, 2000);
        } else {
          alertRef.current.triggerAlert(
            JSON.stringify({
              message: result.msg,
              messageType: "error",
            })
          );
          setLoading(false);
        }
      } catch (error) {
        alertRef.current.triggerAlert(
          JSON.stringify({
            message: "Something went wrong. Please try again.",
            messageType: "error",
          })
        );
        setLoading(false);
      } finally {
        setSubmitting(false);
      }
    },
  });

  // ---- Selling price: without VAT / with VAT ----
  const round2 = (n) => Math.round(n * 100) / 100;
  const vatPct = parseFloat(formik.values.tax_percent) || 0;
  const taxChosen = formik.values.tax_percent !== "" && formik.values.tax_percent != null;
  // Nothing to convert at 0% or with no VAT chosen, so the choice is hidden then.
  const showVatChoice = taxChosen && vatPct > 0;
  const baseFromTyped = (raw, pct) => {
    const n = parseFloat(raw);
    if (!(n >= 0)) return "";
    return String(round2(n / (1 + pct / 100)));
  };
  const handleSellingChange = (raw) => {
    if (priceMode === "excl") {
      formik.setFieldValue("selling_price", raw);
      return;
    }
    setSellingInput(raw);
    formik.setFieldValue("selling_price", baseFromTyped(raw, vatPct));
  };
  const handleTaxChange = (value) => {
    formik.setFieldValue("tax_percent", value);
    if (value === "" || !(parseFloat(value) > 0)) {
      // No VAT (or 0%): the price is the price — keep what is saved, drop the choice.
      setPriceMode("excl");
    } else if (priceMode === "incl") {
      formik.setFieldValue("selling_price", baseFromTyped(sellingInput, parseFloat(value) || 0));
    }
  };
  const handlePriceModeChange = (mode) => {
    if (!mode || mode === priceMode) return;
    // The typed number stays as it is; only what it MEANS changes.
    if (mode === "incl") {
      const typed = formik.values.selling_price;
      setSellingInput(typed === "" || typed == null ? "" : String(typed));
      formik.setFieldValue("selling_price", baseFromTyped(typed, vatPct));
    } else {
      formik.setFieldValue("selling_price", sellingInput);
    }
    setPriceMode(mode);
  };
  const baseNum = parseFloat(formik.values.selling_price);
  const withVatNum = baseNum >= 0 ? round2(baseNum * (1 + vatPct / 100)) : null;

  const fetchProducts = async (search = "") => {
    try {
      const result = await apiData(
        "get",
        `${fetchProductDropdownAPI}?search=${search}`
      );

      if (result.success) {
        // Bundles are a combination of products and cannot be stocked in a
        // store, so exclude them from the stock adjustment dropdown.
        const products = (result.data.result || []).filter(
          (p) =>
            !Number(p.is_bundle) &&
            p.color !== "Bundle" &&
            p.size !== "Bundle"
        );
        setProductOptions(products);
      }
    } catch (error) {
      console.error("Failed to fetch units:", error);
    }
  };

  // Buying/selling price is NOT stored on tbl_product_master — it's recorded
  // per store when stock is added (tbl_price_history, with tbl_stock_transactions
  // as fallback). The product dropdown's buying_price/selling_price columns are
  // blank on live, so pre-fill the price from the store-aware endpoint instead,
  // keyed on the selected store + product. The endpoint falls back gracefully
  // (this store → any store → product master) so it always returns something
  // sensible, which the user can still override.
  const fetchStorePrice = async (productId, storeId) => {
    if (!productId || !storeId) return;
    try {
      const result = await apiData(
        "get",
        `${fetchProductUnitAPI}/${productId}/${storeId}`
      );
      if (result.success) {
        const row = result.data?.[0] || {};
        formik.setFieldValue(
          "buying_price",
          row.buying_price ? parseFloat(row.buying_price) : 0
        );
        formik.setFieldValue(
          "selling_price",
          row.selling_price ? parseFloat(row.selling_price) : 0
        );
        setPriceMode("excl");
        setSellingInput("");
      }
    } catch (error) {
      console.error("Failed to fetch store price:", error);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    if (isNaN(date)) return dateStr; // fallback
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  const fetchStores = async () => {
    try {
      const result = await apiData("get", storesDropdownAPI);
      if (result.success) {
        setStoreOptions(result.data);
      }
    } catch (error) {
      console.error("Failed to fetch stores:", error);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchStores();
  }, []);

  const groupedData = Object.values(
  stockData.reduce((acc, item) => {
    const formattedDate = formatDate(item.manufacture_date);

    const key = `${item.product_id}-${item.store_id}-${item.manufacture_date}-${item.life_days}-${item.buying_price}`;

    if (!acc[key]) {
      acc[key] = {
        ...item,
        formatted_date: formattedDate,
      };
    } else {
      if (new Date(item.created_at) > new Date(acc[key].created_at)) {
        acc[key].selling_price = item.selling_price;
      }
    }
    return acc;
  }, {})
);

  // --- Deduct: the existing batches that actually have stock to pull from,
  // grouped by manufacture date + expiry days (summed across price variants,
  // which is exactly how the backend matches a deduct). This feeds the
  // in-form "Select Batch" picker so the user never has to open Check Expiry
  // or type an expiry date to deduct. Earliest-expiry first. ---
  const computeExpiry = (mfg, life) => {
    if (!mfg || !(Number(life) > 0)) return null;
    // Matches the backend: expiry = manufacture date + (life_days - 1) days.
    return dayjs(mfg).add(Number(life) - 1, "day");
  };

  const deductBatches = Object.values(
    stockData.reduce((acc, item) => {
      const key = `${item.manufacture_date}|${item.life_days}`;
      if (!acc[key]) {
        acc[key] = {
          key,
          manufacture_date: item.manufacture_date,
          life_days: item.life_days,
          available: 0,
        };
      }
      acc[key].available += Number(item.stock_count || 0);
      return acc;
    }, {})
  )
    .filter((b) => b.available > 0)
    .map((b) => {
      const exp = computeExpiry(b.manufacture_date, b.life_days);
      return {
        ...b,
        expiry: exp,
        mfgLabel: formatDate(b.manufacture_date),
        expiryLabel: exp ? exp.format("DD-MM-YYYY") : "—",
      };
    })
    .sort((a, b) =>
      a.expiry && b.expiry ? a.expiry.valueOf() - b.expiry.valueOf() : 0
    );

  const handleDeductBatchChange = (batch) => {
    setSelectedDeductBatch(batch);
    formik.setFieldValue("manufacture_date", batch ? batch.manufacture_date : "");
    formik.setFieldValue("life_days", batch ? batch.life_days : "");
  };

  // Switching Add <-> Deduct resets the batch choice and the batch-linked
  // fields so one flow never leaks a stale value into the other.
  const handleAdjustmentTypeChange = (value) => {
    formik.setFieldValue("adjustmentType", value);
    setSelectedDeductBatch(null);
    setSelectedRow(null);
    setReadOnlyMode(false);
    if (value === "deduct") {
      formik.setFieldValue("manufacture_date", "");
      formik.setFieldValue("life_days", "");
    } else if (value === "add") {
      formik.setFieldValue("manufacture_date", dayjs().format("YYYY-MM-DD"));
      formik.setFieldValue("life_days", "");
    }
  };

  // Reserved units are committed to pending orders — a deduct must never take
  // the store's physical stock below them. So the ceiling for a deduct is the
  // FREE pool: total In Stock minus Reserved (Expired is still deductable, per
  // the chosen rule). Reservations are per product+store, not per batch, so a
  // batch is capped at min(its own stock, the store's free pool).
  const totalInStock = stockData.reduce(
    (s, item) => s + Number(item.stock_count || 0),
    0
  );
  const maxFreeToDeduct = Math.max(0, totalInStock - Number(reservedQty || 0));
  const deductMax = selectedDeductBatch
    ? Math.min(Number(selectedDeductBatch.available || 0), maxFreeToDeduct)
    : 0;
  // Reserved only actually limits THIS deduct when it pulls the cap below the
  // selected batch's own stock. When the batch itself is the limit (it fits
  // inside the free pool), there's no reason to mention reserved at all.
  const reservedIsBinding =
    !!selectedDeductBatch &&
    deductMax < Number(selectedDeductBatch.available || 0);

  const qtyNum = Number(formik.values.stockAdjustment) || 0;
  const deductOverQty =
    formik.values.adjustmentType === "deduct" &&
    selectedDeductBatch &&
    qtyNum > deductMax;
  // Save is blocked on a deduct until a real batch is chosen and the quantity
  // fits within the free pool — so neither the "No live stock" error nor a
  // deduct into reserved stock can happen.
  const deductBlocked =
    formik.values.adjustmentType === "deduct" &&
    (deductBatches.length === 0 ||
      !selectedDeductBatch ||
      deductOverQty ||
      qtyNum < 1);

  const fetchStockTransferData = async (productId, storeId) => {
    if (!productId || !storeId) return;

    // A different product/store means the previously chosen batch no longer
    // applies — clear it (and its linked fields on a deduct) so a stale
    // manufacture_date/life_days can't be submitted.
    setSelectedDeductBatch(null);
    if (formik.values.adjustmentType === "deduct") {
      formik.setFieldValue("manufacture_date", "");
      formik.setFieldValue("life_days", "");
    }

    try {
      const result = await apiData(
        "get",
        `${stockTransferDataAPI}?product_id=${productId}&store_id=${storeId}`
      );

      if (result.success && result.data) {
        setStockData(result.data);
      } else {
        setStockData([]);
      }
    } catch (error) {
      console.error("Error fetching stock transfer data:", error);
      setStockData([]);
    }
  };

  // How many units are reserved (active CDN holds) for this product+store —
  // the amount a deduct must leave untouched.
  const fetchReserved = async (productId, storeId) => {
    if (!productId || !storeId) {
      setReservedQty(0);
      return;
    }
    try {
      const result = await apiData(
        "get",
        `${reservedStockDetailsAPI}?product_id=${productId}&store_id=${storeId}`
      );
      const rows =
        result.success && Array.isArray(result.data) ? result.data : [];
      setReservedQty(rows.reduce((s, r) => s + Number(r.quantity || 0), 0));
    } catch (error) {
      console.error("Failed to fetch reserved quantity:", error);
      setReservedQty(0);
    }
  };

  useEffect(() => {
    if (formik.values.product_id && formik.values.store_id) {
      fetchStockTransferData(formik.values.product_id, formik.values.store_id);
      fetchReserved(formik.values.product_id, formik.values.store_id);
    }
  }, [formik.values.product_id, formik.values.store_id]);

  const startEditBatch = (item) => {
    setEditingBatchId(item.live_stock_id);
    setEditBuyingPrice(item.buying_price ?? "");
    setEditSellingPrice(item.selling_price ?? "");
  };

  const cancelEditBatch = () => {
    setEditingBatchId(null);
    setEditBuyingPrice("");
    setEditSellingPrice("");
  };

  const saveEditBatch = async () => {
    if (editBuyingPrice === "" || editSellingPrice === "") {
      setMessage("Buying price and selling price are required");
      setMessageType("error");
      setMessageOpen(true);
      return;
    }
    if (Number(editSellingPrice) < Number(editBuyingPrice)) {
      setMessage("Selling price cannot be lesser than buying price");
      setMessageType("error");
      setMessageOpen(true);
      return;
    }
    try {
      setSavingBatchPrice(true);
      const result = await apiData("post", updateStockBatchPriceAPI, {
        live_stock_id: editingBatchId,
        buying_price: Number(editBuyingPrice),
        selling_price: Number(editSellingPrice),
        user_id: user.emp_id,
      });
      if (result.success) {
        setMessage(result.message || "Stock batch price updated successfully");
        setMessageType("success");
        setMessageOpen(true);
        cancelEditBatch();
        // Existing add/deduct history for this batch is untouched — this
        // just refreshes the list so the corrected price shows immediately.
        fetchStockTransferData(formik.values.product_id, formik.values.store_id);
      } else {
        setMessage(result.message || "Failed to update stock batch price");
        setMessageType("error");
        setMessageOpen(true);
      }
    } catch (error) {
      console.error("Error updating stock batch price:", error);
      setMessage("Something went wrong. Please try again.");
      setMessageType("error");
      setMessageOpen(true);
    } finally {
      setSavingBatchPrice(false);
    }
  };

  return (
    <Layout>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Grid className="employee-form bold-header">
        <AlertMessage ref={alertRef} />
        <Grid container spacing={2} alignItems="center">
          {/* LEFT SIDE — Title + Breadcrumb */}
          <Grid item xs={12} md={6} lg={6}>
            <Typography className="page_title">Stock Adjustment</Typography>

            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Stock Adjustment</Typography>
            </Breadcrumbs>
          </Grid>

          {/* RIGHT SIDE — Buttons in single line */}
          <Grid item xs={12} md={6} lg={6}>
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                justifyContent: { xs: "flex-start", md: "flex-end" },
                flexWrap: { xs: "wrap", md: "wrap" },
                alignItems: "center",
              }}
            >
              <LoadingButton
                size="medium"
                variant="contained"
                color="success"
                className="button_first"
                href="/reports/adjustment-report"
                sx={{
                  fontSize: { xs: "0.7rem", md: "0.75rem", lg: "0.8rem" },
                  padding: { xs: "8px 12px", md: "8px 14px", lg: "10px 16px" },
                  lineHeight: 1.2,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                }}
              >
                STOCK ADJUSTMENT REPORT
              </LoadingButton>

              <LoadingButton
                size="medium"
                variant="contained"
                className="button_first"
                href="/inventory/bulk-upload"
                sx={{
                  fontSize: { xs: "0.7rem", md: "0.75rem", lg: "0.8rem" },
                  padding: { xs: "8px 12px", md: "8px 14px", lg: "10px 16px" },
                  lineHeight: 1.2,
                  textTransform: "none",
                  backgroundColor: "#7B1FA2",
                  whiteSpace: "nowrap",
                  "&:hover": { backgroundColor: "#6A1B9A" },
                }}
              >
                BULK UPLOAD
              </LoadingButton>

              <LoadingButton
                size="medium"
                variant="contained"
                className="button_first"
                onClick={() => navigate(-1)}
                startIcon={<ArrowBackIosIcon size={20} />}
                sx={{
                  fontSize: { xs: "0.7rem", md: "0.7rem", lg: "0.8rem" },
                  padding: { xs: "8px 12px", md: "6px 12px", lg: "10px 16px" },
                  lineHeight: 1.2,
                  textTransform: "none",
                }}
              >
                BACK
              </LoadingButton>
            </Box>
          </Grid>
        </Grid>

        <form className="form_design" onSubmit={formik.handleSubmit}>
          <Snackbar
            open={messageOpen}
            autoHideDuration={3000}
            onClose={handleMessageClose}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            style={{ marginTop: "50px" }}
          >
            <Alert
              onClose={handleMessageClose}
              severity={messageType}
              sx={{ width: "100%" }}
            >
              {message}
            </Alert>
          </Snackbar>

          {/* <h3>Inventory Stock Adjustment</h3> */}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Product
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} lg={6} md={6}>
              <Autocomplete
                options={storeOptions}
                getOptionLabel={(option) => option.store_name || ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={
                  storeOptions.find((s) => s.id === formik.values.store_id) ||
                  null
                }
                onChange={(e, newValue) => {
                  const newStoreId = newValue ? newValue.id : "";
                  formik.setFieldValue("store_id", newStoreId);
                  // Price is per store, so refresh it for the already-chosen
                  // product whenever the store changes.
                  if (newStoreId && formik.values.product_id) {
                    fetchStorePrice(formik.values.product_id, newStoreId);
                  }
                }}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    {option.store_name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select a Store"
                    fullWidth
                    error={
                      formik.touched.store_id && Boolean(formik.errors.store_id)
                    }
                    helperText={
                      formik.touched.store_id && formik.errors.store_id
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: <>{params.InputProps.endAdornment}</>,
                    }}
                  />
                )}
                ListboxProps={{
                  style: { maxHeight: 200, overflowY: "auto" },
                }}
              />
            </Grid>

            <Grid item xs={12} lg={6} md={6}>
              <Autocomplete
                options={productOptions}
                getOptionLabel={(option) =>
                  option.product_name
                    ? `${option.product_name} ${option.sku || ""}`
                    : ""
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={loadingProducts}
                value={
                  productOptions.find(
                    (p) => p.id === formik.values.product_id
                  ) || null
                }
                onInputChange={(e, value, reason) => {
                  setSearchTerm(value);
                  if (reason === "input") fetchProducts(value);
                  else if (reason === "clear" || reason === "reset")
                    fetchProducts("");
                }}
                onChange={(e, newValue) => {
                  if (newValue) {
                    formik.setFieldValue("product_id", newValue.id);
                    setPriceMode("excl");
                    // Pull the real price for THIS product in the selected
                    // store (per-store price history), not the blank
                    // product_master columns carried on the dropdown option.
                    if (formik.values.store_id) {
                      fetchStorePrice(newValue.id, formik.values.store_id);
                    } else {
                      formik.setFieldValue("buying_price", "");
                      formik.setFieldValue("selling_price", "");
                    }
                  } else {
                    formik.setFieldValue("product_id", "");
                    formik.setFieldValue("buying_price", "");
                    formik.setFieldValue("selling_price", "");
                    setPriceMode("excl");
                  }
                }}
                renderInput={(params) => {
                  const selected = productOptions.find(
                    (p) => p.id === formik.values.product_id
                  );

                  return (
                    <TextField
                      {...params}
                      label="Select a Product"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: selected ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                            }}
                          >
                            {selected.product_image && (
                              <img
                                src={selected.product_image}
                                alt={selected.product_name}
                                style={{
                                  width: 36,
                                  height: 36,
                                  objectFit: "contain",
                                  borderRadius: 6,
                                }}
                              />
                            )}
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              <span>
                                {selected.product_name} (SKU: {selected.sku})
                              </span>
                              <span
                                style={{ fontSize: "12px", color: "#1976d2" }}
                              >
                                Size: {selected.size || "NA"}, Colour:{" "}
                                {selected.color || "NA"}
                              </span>
                            </div>
                          </div>
                        ) : null,

                        // hides duplicate text in input when selected
                        inputProps: {
                          ...params.inputProps,
                          value: selected ? "" : params.inputProps.value,
                        },
                      }}
                    />
                  );
                }}
                // ⭐ HIGHLIGHT SELECTED ITEM IN DROPDOWN
                renderOption={(props, option, state) => {
                  const isSelected = formik.values.product_id === option.id;

                  return (
                    <li
                      {...props}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "10px",
                        background: isSelected ? "#e6f3ff" : "white",
                        borderLeft: isSelected
                          ? "4px solid #1976d2"
                          : "4px solid transparent",
                      }}
                    >
                      {option.product_image && (
                        <img
                          src={option.product_image}
                          alt={option.product_name}
                          style={{
                            width: 40,
                            height: 40,
                            marginRight: 12,
                            objectFit: "contain",
                          }}
                        />
                      )}

                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <strong>
                          {option.product_name}{" "}
                          <span style={{ color: "#555" }}>
                            (SKU: {option.sku})
                          </span>
                        </strong>
                        {option.size === "Bundle" ||
                        option.color === "Bundle" ? (
                          <Chip
                            label="Bundle"
                            size="small"
                            color="secondary"
                            sx={{
                              height: 18,
                              width: "fit-content",
                              mt: 0.3,
                              fontSize: "0.65rem",
                              fontWeight: 700,
                            }}
                          />
                        ) : (
                          <span
                            style={{ fontSize: "13px", color: "#007bff" }}
                          >
                            Size: {option.size}, Colour: {option.color}
                          </span>
                        )}
                      </div>
                    </li>
                  );
                }}
                ListboxProps={{
                  style: { maxHeight: 200, overflowY: "auto" },
                }}
              />
            </Grid>

            {stockData.length > 0 && (
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-start" pr={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleOpen}
                  >
                    Check Expiry
                  </Button>
                </Box>
              </Grid>
            )}

            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
              <DialogTitle>Select Product to Check Expiry</DialogTitle>
              <DialogContent>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Select</TableCell>
                      <TableCell>Manufacture Date</TableCell>
                      <TableCell>Expiry Days</TableCell>
                      <TableCell>Buying Price</TableCell>
                      <TableCell>Selling Price</TableCell>
                      <TableCell>Stock Count</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupedData.map((item) => {
                      const isEditing = editingBatchId === item.live_stock_id;
                      return (
                        <TableRow
                          key={`${item.product_id}-${item.store_id}-${item.manufacture_date}-${item.life_days}`}
                        >
                          <TableCell>
                            <Checkbox
                              checked={
                                selectedRow ===
                                `${item.product_id}-${item.store_id}-${item.manufacture_date}-${item.life_days}-${item.buying_price}`
                              }
                              disabled={isEditing}
                              onChange={() => handleSelect(item)}
                            />
                          </TableCell>
                          <TableCell>{item.formatted_date}</TableCell>
                          <TableCell>{item.life_days}</TableCell>
                          <TableCell>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editBuyingPrice}
                                onChange={(e) => setEditBuyingPrice(e.target.value)}
                                sx={{ width: 100 }}
                              />
                            ) : (
                              item.buying_price
                            )}
                          </TableCell>
                          <TableCell>
                            {isEditing ? (
                              <TextField
                                size="small"
                                type="number"
                                value={editSellingPrice}
                                onChange={(e) => setEditSellingPrice(e.target.value)}
                                sx={{ width: 100 }}
                              />
                            ) : (
                              formatAED(item.selling_price)
                            )}
                          </TableCell>
                          <TableCell>{formatNumber(item.stock_count)}</TableCell>
                          <TableCell align="right">
                            {isEditing ? (
                              <>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  disabled={savingBatchPrice}
                                  onClick={saveEditBatch}
                                >
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  disabled={savingBatchPrice}
                                  onClick={cancelEditBatch}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </>
                            ) : (
                              <IconButton
                                size="small"
                                title="Edit price"
                                onClick={() => startEditBatch(item)}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose}>Close</Button>
              </DialogActions>
            </Dialog>

            {formik.values.adjustmentType !== "deduct" && (
              <>
            <Grid item xs={12} md={6} lg={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MobileDatePicker
                  label="Manufacture Date"
                  value={
                    formik.values.manufacture_date
                      ? dayjs(formik.values.manufacture_date)
                      : null
                  }
                  inputFormat="DD-MM-YYYY"
                  onChange={(date) => {
                    formik.setFieldValue(
                      "manufacture_date",
                      date ? date.format("YYYY-MM-DD") : ""
                    );
                  }}
                  disabled={readOnlyMode}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      InputProps={{ readOnly: readOnlyMode }}
                      error={
                        formik.touched.manufacture_date &&
                        Boolean(formik.errors.manufacture_date)
                      }
                      helperText={
                        formik.touched.manufacture_date &&
                        formik.errors.manufacture_date
                      }
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} lg={6} md={6}>
              <TextField
                name="life_days"
                type="number"
                label="Expiry Days"
                fullWidth
                value={formik.values.life_days}
                onChange={formik.handleChange}
                disabled={readOnlyMode}
                error={
                  formik.touched.life_days && Boolean(formik.errors.life_days)
                }
                helperText={formik.touched.life_days && formik.errors.life_days}
              />
            </Grid>
              </>
            )}

            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="note"
                label="Reference Note"
                fullWidth
                multiline
                rows={2}
                value={formik.values.note}
                onChange={formik.handleChange}
              />
            </Grid>

            <Grid item xs={12} mt={4}>
              <Typography variant="h6" gutterBottom>
                Stock Adjustment
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <FormControl
                fullWidth
                error={
                  formik.touched.adjustmentType &&
                  Boolean(formik.errors.adjustmentType)
                }
                sx={{
                  position: "relative",

                  // hide clear button normally
                  "& .clear-adjustment-btn": {
                    visibility: "hidden",
                    opacity: 0,
                    transition: "opacity 0.2s ease",
                  },

                  // show clear button when hovering over the input area
                  "&:hover .clear-adjustment-btn": {
                    visibility: "visible",
                    opacity: 1,
                  },
                }}
              >
                <InputLabel>Adjustment Type</InputLabel>
                <Select
                  labelId="adjustment-type-label"
                  name="adjustmentType"
                  value={formik.values.adjustmentType}
                  onChange={(e) => handleAdjustmentTypeChange(e.target.value)}
                  onBlur={formik.handleBlur}
                  label="Adjustment Type"
                  endAdornment={
                    formik.values.adjustmentType && (
                      <InputAdornment position="end">
                        <IconButton
                          className="clear-adjustment-btn"
                          size="small"
                          onClick={() => handleAdjustmentTypeChange("")}
                          onMouseDown={(e) => e.stopPropagation()}
                          sx={{ mr: "-10px" }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                  sx={{
                    paddingRight: formik.values.adjustmentType
                      ? "40px"
                      : "14px",
                  }}
                >
                  <MenuItem value="add">Add</MenuItem>
                  <MenuItem value="deduct">Deduct</MenuItem>
                </Select>

                {formik.touched.adjustmentType &&
                  formik.errors.adjustmentType && (
                    <Typography variant="caption" color="error">
                      {formik.errors.adjustmentType}
                    </Typography>
                  )}
              </FormControl>
            </Grid>

            {formik.values.adjustmentType === "deduct" && (
              <Grid item xs={12} lg={6} md={6}>
                {!formik.values.product_id || !formik.values.store_id ? (
                  <Alert severity="info">
                    Select a store and product to see available batches.
                  </Alert>
                ) : deductBatches.length === 0 ? (
                  <Alert severity="warning">
                    No stock available in this store to deduct.
                  </Alert>
                ) : (
                  <Autocomplete
                    options={deductBatches}
                    value={selectedDeductBatch}
                    isOptionEqualToValue={(o, v) => o.key === v.key}
                    getOptionLabel={(b) =>
                      b
                        ? `Mfg ${b.mfgLabel} · Expires ${b.expiryLabel} · ${formatNumber(
                            b.available
                          )} available`
                        : ""
                    }
                    onChange={(e, v) => handleDeductBatchChange(v)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Batch to Deduct From"
                        required
                        error={!selectedDeductBatch}
                        helperText={
                          selectedDeductBatch
                            ? `${formatNumber(
                                selectedDeductBatch.available
                              )} available in this batch`
                            : "Choose which batch to deduct from"
                        }
                      />
                    )}
                  />
                )}
              </Grid>
            )}

            {formik.values.adjustmentType === "add" && (
              <>
                <Grid item xs={12}>
                  <Box sx={{ border: "1px solid #E3E6EA", borderRadius: "10px", p: 2.5, bgcolor: "#FAFBFC" }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.4, color: "#475569", textTransform: "uppercase", mb: 2 }}>
                      Pricing
                    </Typography>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          name="buying_price"
                          type="number"
                          label="Buying Price"
                          fullWidth
                          value={formik.values.buying_price}
                          onChange={formik.handleChange}
                          error={formik.touched.buying_price && Boolean(formik.errors.buying_price)}
                          helperText={formik.touched.buying_price && formik.errors.buying_price}
                          sx={{ bgcolor: "#fff" }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          select
                          name="tax_percent"
                          label="Tax (VAT) %"
                          fullWidth
                          value={formik.values.tax_percent}
                          onChange={(e) => handleTaxChange(e.target.value)}
                          SelectProps={{ displayEmpty: true }}
                          InputLabelProps={{ shrink: true }}
                          sx={{ bgcolor: "#fff" }}
                        >
                          <MenuItem value="">Not set (choose in the form)</MenuItem>
                          <MenuItem value="0">0% (Zero rated)</MenuItem>
                          <MenuItem value="5">5% VAT</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          name="selling_price_input"
                          type="number"
                          label={
                            !showVatChoice
                              ? "Selling Price"
                              : priceMode === "incl"
                              ? "Selling Price (with VAT)"
                              : "Selling Price (without VAT)"
                          }
                          fullWidth
                          value={priceMode === "incl" && showVatChoice ? sellingInput : formik.values.selling_price}
                          onChange={(e) => handleSellingChange(e.target.value)}
                          onBlur={() => formik.setFieldTouched("selling_price", true)}
                          error={formik.touched.selling_price && Boolean(formik.errors.selling_price)}
                          helperText={formik.touched.selling_price ? formik.errors.selling_price : undefined}
                          sx={{ bgcolor: "#fff" }}
                        />
                      </Grid>
                    </Grid>

                    {showVatChoice && (
                      <Box
                        sx={{
                          mt: 2.5,
                          pt: 2,
                          borderTop: "1px dashed #D5DAE0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: 2,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Typography sx={{ fontSize: 13.5, color: "#475569" }}>The selling price I typed is</Typography>
                          <ToggleButtonGroup
                            exclusive
                            size="small"
                            color="primary"
                            value={priceMode}
                            onChange={(e, v) => handlePriceModeChange(v)}
                            aria-label="Is the typed price with or without VAT"
                            sx={{ bgcolor: "#fff", "& .MuiToggleButton-root": { textTransform: "none", px: 2, fontWeight: 600 } }}
                          >
                            <ToggleButton value="excl">Without VAT</ToggleButton>
                            <ToggleButton value="incl">With VAT</ToggleButton>
                          </ToggleButtonGroup>
                        </Box>
                        <Box sx={{ display: "flex", gap: 4 }}>
                          <Box>
                            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Without VAT (saved)</Typography>
                            <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                              {baseNum >= 0 ? formatAED(baseNum) : "-"}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>With VAT ({vatPct}%)</Typography>
                            <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                              {withVatNum != null ? formatAED(withVatNum) : "-"}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </>
            )}

            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="stockAdjustment"
                type="number"
                label="Quantity"
                fullWidth
                value={formik.values.stockAdjustment}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  (formik.touched.stockAdjustment &&
                    Boolean(formik.errors.stockAdjustment)) ||
                  deductOverQty
                }
                helperText={
                  deductOverQty
                    ? reservedIsBinding
                      ? `Max ${formatNumber(
                          deductMax
                        )} — ${formatNumber(
                          reservedQty
                        )} reserved for pending orders can't be deducted`
                      : `Only ${formatNumber(
                          deductMax
                        )} available in the selected batch`
                    : formik.values.adjustmentType === "deduct" &&
                      selectedDeductBatch
                    ? `Up to ${formatNumber(deductMax)} can be deducted${
                        reservedIsBinding
                          ? ` (${formatNumber(
                              reservedQty
                            )} reserved for pending orders)`
                          : ""
                      }`
                    : formik.touched.stockAdjustment &&
                      formik.errors.stockAdjustment
                }
              />
            </Grid>

            {/* <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.out_of_stock === 1}
                    onChange={(e) =>
                      formik.setFieldValue(
                        "out_of_stock",
                        e.target.checked ? 1 : 0
                      )
                    }
                  />
                }
                label="Mark as Out of Stock"
              />
            </Grid> */}

            {/* Submit Button */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="center" mt={5} pr={2}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading || deductBlocked}
                >
                  Save Stock
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </Layout>
  );
};

export default InventoryManagement;
